import React, { useState, useEffect } from 'react';
import { getClients, createClient, type Client } from '@/lib/api/clients';
import { getJobs, type Job } from '@/lib/api/jobs';
import { getScheduleEventsByJob, type ScheduleEvent } from '@/lib/api/schedule';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';

interface SubtaskData {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  checklistItems: { id: string; text: string; checked: boolean }[];
}

interface TaskData {
  id: string;
  name: string;
  description: string;
  status: string;
  priority: 'low' | 'medium' | 'high';
  startDate: string;
  endDate: string;
  subtasks: SubtaskData[];
  expanded?: boolean;
}

interface JobSchedulingFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  isSubmitting?: boolean;
  initialData?: any;
  isEditMode?: boolean;
  editingTaskId?: string | null;
  /** Optional extra content rendered at the top of the form body (e.g. worker assignment). */
  extraSection?: React.ReactNode;
}

export const JobSchedulingForm = ({ isOpen, onClose, onSubmit, isSubmitting = false, initialData, isEditMode = false, editingTaskId, extraSection }: JobSchedulingFormProps) => {
  // Overall Job Scheduling state
  const [jobName, setJobName] = useState('');
  const [jobId, setJobId] = useState('');
  const [clientName, setClientName] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [jobStartDate, setJobStartDate] = useState('');
  const [jobEndDate, setJobEndDate] = useState('');
  const [startDateUnknown, setStartDateUnknown] = useState(false);
  const [endDateUnknown, setEndDateUnknown] = useState(false);
  
  // Dropdown data
  const [clients, setClients] = useState<Client[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [, setLoadingJobs] = useState(false);
  
  // New client/job modal
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [creatingClient, setCreatingClient] = useState(false);
  
  // Job Site Logistics state
  const [siteAddress, setSiteAddress] = useState('');
  const [siteCity, setSiteCity] = useState('');
  const [siteProvince, setSiteProvince] = useState('BC');
  const [sitePostalCode, setSitePostalCode] = useState('');
  const [parkingInstructions, setParkingInstructions] = useState('');
  const [accessCodes, setAccessCodes] = useState('');
  const [deliveryWindows, setDeliveryWindows] = useState('');
  const [clientNotes, setClientNotes] = useState('');
  const [safetyNotes, setSafetyNotes] = useState('');
  
  // Task Management state
  const [tasks, setTasks] = useState<TaskData[]>([]);
  
  // Accordion state
  const [overallJobOpen, setOverallJobOpen] = useState(true);
  const [logisticsOpen, setLogisticsOpen] = useState(false);
  const [taskManagementOpen, setTaskManagementOpen] = useState(true);
  
  // Add Task Overlay state
  const [showAddTaskOverlay, setShowAddTaskOverlay] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskData | null>(null);
  const [formError, setFormError] = useState('');
  const [clientError, setClientError] = useState('');
  const [confirmDeleteTaskId, setConfirmDeleteTaskId] = useState<string | null>(null);
  
  // Task form state
  const [taskName, setTaskName] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskStatus, setTaskStatus] = useState('scheduled');
  const [taskPriority, setTaskPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [taskStartDate, setTaskStartDate] = useState('');
  const [taskEndDate, setTaskEndDate] = useState('');
  const [taskSubtasks, setTaskSubtasks] = useState<SubtaskData[]>([]);

  useEffect(() => {
    if (isOpen) {
      loadClients();
      loadJobs();
      
      // Populate form if editing
      if (isEditMode && initialData) {
        setJobName(initialData.title || '');
        setJobId(initialData.jobNumber || '');
        setSelectedClientId(initialData.clientId?.toString() || '');
        setClientName(initialData.client || '');
        setJobStartDate(initialData.startDate ? new Date(initialData.startDate).toISOString().split('T')[0] : '');
        setJobEndDate(initialData.estimatedEndDate ? new Date(initialData.estimatedEndDate).toISOString().split('T')[0] : '');
        setStartDateUnknown(!initialData.startDate);
        setEndDateUnknown(!initialData.estimatedEndDate);
        setSiteAddress(initialData.siteAddress || '');
        setSiteCity(initialData.siteCity || '');
        setSiteProvince(initialData.siteProvince || 'BC');
        setSitePostalCode(initialData.sitePostalCode || '');
        setParkingInstructions(initialData.parkingInstructions || '');
        setAccessCodes(initialData.accessCodes || '');
        setDeliveryWindows(initialData.deliveryWindows || '');
        setClientNotes(initialData.clientNotes || '');
        setSafetyNotes(initialData.safetyNotes || '');
        
        // Load existing schedule events as tasks
        loadExistingTasks(initialData.id);
      } else {
        // Clear tasks when opening for new job
        setTasks([]);
        setStartDateUnknown(false);
        setEndDateUnknown(false);
      }
    }
  }, [isOpen, isEditMode, initialData]);

  // Auto-open task edit form if editingTaskId is provided
  useEffect(() => {
    if (editingTaskId === '__new__') {
      // Open add new task form
      handleAddTaskClick();
    } else if (editingTaskId && tasks.length > 0) {
      // Open edit existing task form
      const taskToEdit = tasks.find(t => t.id === editingTaskId);
      if (taskToEdit) {
        handleEditTaskClick(taskToEdit);
      }
    }
  }, [editingTaskId, tasks]);

  const loadExistingTasks = async (jobId: string | number) => {
    try {
      const events = await getScheduleEventsByJob(jobId.toString());
      
      // Group consecutive schedule events by task name to reconstruct multi-day tasks
      const taskGroups = new Map<string, ScheduleEvent[]>();
      
      events.forEach((event: ScheduleEvent) => {
        const taskName = event.notes || `Task ${event.id}`;
        if (!taskGroups.has(taskName)) {
          taskGroups.set(taskName, []);
        }
        taskGroups.get(taskName)!.push(event);
      });
      
      // Convert grouped events to tasks
      const loadedTasks: TaskData[] = Array.from(taskGroups.entries()).map(([name, eventGroup]) => {
        // Sort by date
        eventGroup.sort((a, b) => {
          const dateA = a.scheduled_date || a.scheduledDate;
          const dateB = b.scheduled_date || b.scheduledDate;
          return dateA.localeCompare(dateB);
        });
        
        const firstEvent = eventGroup[0];
        const lastEvent = eventGroup[eventGroup.length - 1];
        
        // Convert subtasks from first event (subtasks are only stored on first day)
        const formatDateForInput = (dateStr: string) => {
          if (!dateStr) return '';
          // If it's already in YYYY-MM-DD format, use it
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
          // Otherwise parse and format
          try {
            return new Date(dateStr).toISOString().split('T')[0];
          } catch {
            return dateStr;
          }
        };
        
        const subtasks: SubtaskData[] = (firstEvent.subtasks || []).map((st: any) => ({
          id: st.id?.toString() || `subtask-${Date.now()}`,
          title: st.title,
          startDate: formatDateForInput(st.start_date || st.startDate || ''),
          endDate: formatDateForInput(st.end_date || st.endDate || ''),
          checklistItems: (st.checklistItems || []).map((item: any) => ({
            id: item.id?.toString() || `item-${Date.now()}`,
            text: item.text,
            checked: Boolean(item.checked)
          })),
        }));
        
        return {
          id: firstEvent.id.toString(),
          name: name,
          description: '', // Description is stored separately in the task form, not in schedule notes
          status: firstEvent.status || 'scheduled',
          priority: 'medium' as const,
          startDate: firstEvent.scheduled_date || firstEvent.scheduledDate || '',
          endDate: lastEvent.scheduled_date || lastEvent.scheduledDate || '',
          subtasks: subtasks,
          expanded: false,
        };
      });
      
      setTasks(loadedTasks);
    } catch (error) {
      console.error('Failed to load existing tasks:', error);
      // Don't fail the whole form, just log the error
    }
  };

  const loadClients = async () => {
    try {
      setLoadingClients(true);
      const data = await getClients();
      setClients(data);
    } catch (error) {
      console.error('Failed to load clients:', error);
    } finally {
      setLoadingClients(false);
    }
  };

  const loadJobs = async () => {
    try {
      setLoadingJobs(true);
      const response = await getJobs({ limit: 100 });
      setJobs(response.jobs);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleCreateNewClient = async () => {
    if (!newClientName.trim()) return;
    
    try {
      setCreatingClient(true);
      const newClient = await createClient({
        name: newClientName,
        email: newClientEmail || undefined,
        phone: newClientPhone || undefined,
      });
      
      // Add to clients list
      setClients([...clients, newClient]);
      
      // Select the new client
      setSelectedClientId(newClient.id);
      setClientName(newClient.name);
      
      // Close modal and reset
      setShowNewClientModal(false);
      setNewClientName('');
      setNewClientEmail('');
      setNewClientPhone('');
    } catch (error) {
      console.error('Failed to create client:', error);
      setClientError('Failed to create client. Please try again.');
    } finally {
      setCreatingClient(false);
    }
  };

  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    
    if (value === '__new__') {
      setShowNewClientModal(true);
      return;
    }
    
    setSelectedClientId(value);
    const selectedClient = clients.find(c => c.id === value);
    if (selectedClient) {
      setClientName(selectedClient.name);
    } else {
      setClientName('');
    }
  };

  const handleCustomJobNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setJobName(e.target.value);
  };

  const handleJobIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setJobId(e.target.value);
  };

  const handleAddTaskClick = () => {
    // Reset task form
    setTaskName('');
    setTaskDescription('');
    setTaskStatus('scheduled');
    setTaskPriority('medium');
    setTaskStartDate('');
    setTaskEndDate('');
    setTaskSubtasks([]);
    setEditingTask(null);
    setShowAddTaskOverlay(true);
  };

  const handleEditTaskClick = (task: TaskData) => {
    setTaskName(task.name);
    setTaskDescription(task.description);
    setTaskStatus(task.status);
    setTaskPriority(task.priority);
    
    // Format dates to YYYY-MM-DD for date inputs
    const formatDateForInput = (dateStr: string) => {
      if (!dateStr) return '';
      // If it's already in YYYY-MM-DD format, use it
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      // Otherwise parse and format
      try {
        return new Date(dateStr).toISOString().split('T')[0];
      } catch {
        return dateStr;
      }
    };
    
    setTaskStartDate(formatDateForInput(task.startDate));
    setTaskEndDate(formatDateForInput(task.endDate));
    setTaskSubtasks(task.subtasks);
    setEditingTask(task);
    setShowAddTaskOverlay(true);
  };

  const handleTaskSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const taskData: TaskData = {
      id: editingTask?.id || `task-${Date.now()}`,
      name: taskName,
      description: taskDescription,
      status: taskStatus,
      priority: taskPriority,
      startDate: taskStartDate,
      endDate: taskEndDate,
      subtasks: taskSubtasks,
    };

    if (editingTask) {
      // Update existing task
      setTasks(tasks.map(t => t.id === editingTask.id ? taskData : t));
    } else {
      // Add new task
      setTasks([...tasks, taskData]);
    }

    setShowAddTaskOverlay(false);
  };

  const handleDeleteTask = (taskId: string) => {
    setConfirmDeleteTaskId(taskId);
  };

  const handleDuplicateTask = (task: TaskData) => {
    const duplicatedTask: TaskData = {
      ...task,
      id: `task-${Date.now()}`,
      name: `${task.name} (Copy)`,
      expanded: false,
    };
    setTasks([...tasks, duplicatedTask]);
  };

  const toggleTaskExpanded = (taskId: string) => {
    setTasks(tasks.map(t => 
      t.id === taskId ? { ...t, expanded: !t.expanded } : t
    ));
  };

  const addSubtask = () => {
    setTaskSubtasks([
      ...taskSubtasks,
      {
        id: `subtask-${Date.now()}`,
        title: '',
        startDate: '',
        endDate: '',
        checklistItems: [],
      },
    ]);
  };

  const removeSubtask = (id: string) => {
    setTaskSubtasks(taskSubtasks.filter((s) => s.id !== id));
  };

  const updateSubtask = (id: string, field: keyof SubtaskData, value: any) => {
    setTaskSubtasks(
      taskSubtasks.map((s) => (s.id === id ? { ...s, [field]: value } : s))
    );
  };

  const addChecklistItem = (subtaskId: string) => {
    setTaskSubtasks(
      taskSubtasks.map((s) =>
        s.id === subtaskId
          ? {
              ...s,
              checklistItems: [
                ...s.checklistItems,
                { id: `item-${Date.now()}`, text: '', checked: false },
              ],
            }
          : s
      )
    );
  };

  const removeChecklistItem = (subtaskId: string, itemId: string) => {
    setTaskSubtasks(
      taskSubtasks.map((s) =>
        s.id === subtaskId
          ? {
              ...s,
              checklistItems: s.checklistItems.filter((item) => item.id !== itemId),
            }
          : s
      )
    );
  };

  const updateChecklistItem = (
    subtaskId: string,
    itemId: string,
    field: 'text' | 'checked',
    value: string | boolean
  ) => {
    setTaskSubtasks(
      taskSubtasks.map((s) =>
        s.id === subtaskId
          ? {
              ...s,
              checklistItems: s.checklistItems.map((item) =>
                item.id === itemId ? { ...item, [field]: value } : item
              ),
            }
          : s
      )
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    // Validate required fields
    if (!jobName || !jobName.trim()) {
      setFormError('Job Name is required.');
      return;
    }

    if (!clientName && !selectedClientId) {
      setFormError('Please select a client.');
      return;
    }
    
    onSubmit({
      jobName,
      jobId,
      clientName,
      clientId: selectedClientId || undefined,
      jobStartDate: startDateUnknown ? '' : jobStartDate,
      jobEndDate: endDateUnknown ? '' : jobEndDate,
      siteAddress,
      siteCity,
      siteProvince,
      sitePostalCode,
      parkingInstructions,
      accessCodes,
      deliveryWindows,
      clientNotes,
      safetyNotes,
      tasks,
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-blue-100 text-blue-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Scheduled';
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Main Form - Centered Overlay Popup */}
      <div 
        className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto p-4"
        onClick={onClose}
      >
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-7xl my-8 flex flex-col max-h-[90vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <header className="sticky top-0 z-10 bg-white border-b border-gray-200 p-4 rounded-t-xl">
            <div className="flex items-center justify-between">
              <div className="flex gap-3 items-center">
                <div className="flex flex-col">
                  <h1 className="text-gray-900 text-base font-medium leading-normal">
                    {isEditMode ? 'Edit Job Schedule' : 'Schedule New Job'}
                  </h1>
                  <p className="text-gray-500 text-sm font-normal leading-normal">
                    {isEditMode ? 'Update job scheduling details' : 'Fill in details below'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm font-bold rounded-DEFAULT text-gray-700 bg-gray-200/50 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-6 py-2 text-sm font-bold rounded-DEFAULT bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting && (
                    <span className="animate-spin">⏳</span>
                  )}
                  {isSubmitting ? (isEditMode ? 'Updating...' : 'Scheduling...') : (isEditMode ? 'Update Schedule' : 'Schedule Job')}
                </button>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-6 bg-gray-50">
            {formError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{formError}</div>
            )}
            {extraSection && (
              <section className="mb-6 bg-white border border-gray-200 rounded-xl p-4">
                <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <span className="material-symbols-outlined text-base">manage_accounts</span>
                  Assign Individual Workers
                </h2>
                {extraSection}
              </section>
            )}
            <div className="flex flex-col gap-6">

              <div className="flex flex-col gap-4">
                {/* Overall Job Scheduling Section */}
                <details open={overallJobOpen} className="bg-white rounded-lg border border-gray-200">
                  <summary
                    className="flex items-center justify-between px-6 py-4 cursor-pointer select-none hover:bg-gray-50"
                    onClick={(e) => {
                      e.preventDefault();
                      setOverallJobOpen(!overallJobOpen);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-primary">event</span>
                      <h2 className="text-lg font-bold">Overall Job Scheduling</h2>
                    </div>
                    <span className="material-symbols-outlined text-gray-500">
                      {overallJobOpen ? 'expand_less' : 'expand_more'}
                    </span>
                  </summary>
                  {overallJobOpen && (
                    <div className="px-6 pb-6 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Job ID</label>
                          <input
                            type="text"
                            className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="Optional - Auto-generated if blank"
                            value={jobId}
                            onChange={handleJobIdChange}
                            disabled={isEditMode}
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {isEditMode ? 'Job ID cannot be changed' : 'Leave blank to auto-generate (e.g., JOB0001)'}
                          </p>
                        </div>
                        <div>
                          <label className="text-sm font-medium">Client Name</label>
                          <select
                            className="form-select mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                            value={selectedClientId}
                            onChange={handleClientChange}
                            disabled={loadingClients}
                          >
                            <option value="">Select client...</option>
                            <option value="__new__">+ Create New Client</option>
                            <optgroup label="Existing Clients">
                              {clients.map((client) => (
                                <option key={client.id} value={client.id}>
                                  {client.name}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Job Name</label>
                        <div className="relative">
                          <input
                            type="text"
                            className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white pr-10"
                            placeholder="Type job name or select from existing..."
                            value={jobName}
                            onChange={handleCustomJobNameChange}
                            list="job-names-list"
                          />
                          <datalist id="job-names-list">
                            {jobs.map((job) => (
                              <option key={job.id} value={job.title} />
                            ))}
                          </datalist>
                          {jobName && (
                            <button
                              type="button"
                              onClick={() => setJobName('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 text-gray-400 hover:text-gray-600"
                              title="Clear"
                            >
                              <span className="material-symbols-outlined text-base">close</span>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-sm font-medium">Start Date</label>
                            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-primary"
                                checked={startDateUnknown}
                                onChange={(e) => {
                                  setStartDateUnknown(e.target.checked);
                                  if (e.target.checked) setJobStartDate('');
                                }}
                              />
                              Unknown
                            </label>
                          </div>
                          <input
                            type="date"
                            className="form-input w-full rounded-DEFAULT border-gray-300 bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            value={jobStartDate}
                            onChange={(e) => setJobStartDate(e.target.value)}
                            disabled={startDateUnknown}
                          />
                        </div>
                        <div>
                          <div className="flex items-center justify-between mb-1">
                            <label className="text-sm font-medium">End Date</label>
                            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer select-none">
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-primary"
                                checked={endDateUnknown}
                                onChange={(e) => {
                                  setEndDateUnknown(e.target.checked);
                                  if (e.target.checked) setJobEndDate('');
                                }}
                              />
                              Unknown
                            </label>
                          </div>
                          <input
                            type="date"
                            className="form-input w-full rounded-DEFAULT border-gray-300 bg-white disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            value={jobEndDate}
                            onChange={(e) => setJobEndDate(e.target.value)}
                            disabled={endDateUnknown}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </details>

                {/* Job Site Logistics Section */}
                <details open={logisticsOpen} className="bg-white rounded-lg border border-gray-200">
                  <summary
                    className="flex items-center justify-between px-6 py-4 cursor-pointer select-none hover:bg-gray-50"
                    onClick={(e) => {
                      e.preventDefault();
                      setLogisticsOpen(!logisticsOpen);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-primary">location_on</span>
                      <h2 className="text-lg font-bold">Job Site Logistics & Client Notes</h2>
                    </div>
                    <span className="material-symbols-outlined text-gray-500">
                      {logisticsOpen ? 'expand_less' : 'expand_more'}
                    </span>
                  </summary>
                  {logisticsOpen && (
                    <div className="px-6 pb-6 space-y-4">
                      {/* Address Fields */}
                      <div>
                        <label className="text-sm font-medium">Street Address</label>
                        <input
                          type="text"
                          className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                          placeholder="123 Main Street"
                          value={siteAddress}
                          onChange={(e) => setSiteAddress(e.target.value)}
                        />
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium">City</label>
                          <input
                            type="text"
                            className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                            placeholder="Vancouver"
                            value={siteCity}
                            onChange={(e) => setSiteCity(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Province</label>
                          <select
                            className="form-select mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                            value={siteProvince}
                            onChange={(e) => setSiteProvince(e.target.value)}
                          >
                            <option value="">Select Province</option>
                            <option value="AB">Alberta</option>
                            <option value="BC">British Columbia</option>
                            <option value="MB">Manitoba</option>
                            <option value="NB">New Brunswick</option>
                            <option value="NL">Newfoundland and Labrador</option>
                            <option value="NS">Nova Scotia</option>
                            <option value="ON">Ontario</option>
                            <option value="PE">Prince Edward Island</option>
                            <option value="QC">Quebec</option>
                            <option value="SK">Saskatchewan</option>
                            <option value="NT">Northwest Territories</option>
                            <option value="NU">Nunavut</option>
                            <option value="YT">Yukon</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-medium">Postal Code</label>
                          <input
                            type="text"
                            className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white uppercase"
                            placeholder="V6B 1A1"
                            maxLength={7}
                            value={sitePostalCode}
                            onChange={(e) => setSitePostalCode(e.target.value)}
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium">Parking Instructions</label>
                        <input
                          type="text"
                          className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                          placeholder="Where to park, visitor parking info, street parking notes"
                          value={parkingInstructions}
                          onChange={(e) => setParkingInstructions(e.target.value)}
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Access Codes</label>
                          <input
                            type="text"
                            className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                            placeholder="Gate codes, lockbox combinations, door codes"
                            value={accessCodes}
                            onChange={(e) => setAccessCodes(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Delivery Windows</label>
                          <input
                            type="text"
                            className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                            placeholder="Time restrictions, noise bylaws"
                            value={deliveryWindows}
                            onChange={(e) => setDeliveryWindows(e.target.value)}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Client Instructions & Access Notes</label>
                        <textarea
                          className="form-textarea mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                          rows={3}
                          placeholder="Any special client requests or access information"
                          value={clientNotes}
                          onChange={(e) => setClientNotes(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Safety Notes</label>
                        <textarea
                          className="form-textarea mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                          rows={3}
                          placeholder="Hazards, safety equipment requirements, etc."
                          value={safetyNotes}
                          onChange={(e) => setSafetyNotes(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </details>

                {/* Task Management Section */}
                <details open={taskManagementOpen} className="bg-white rounded-lg border border-gray-200">
                  <summary
                    className="flex items-center justify-between px-6 py-4 cursor-pointer select-none hover:bg-gray-50"
                    onClick={(e) => {
                      e.preventDefault();
                      setTaskManagementOpen(!taskManagementOpen);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-primary">task_alt</span>
                      <h2 className="text-lg font-bold">Task Management</h2>
                    </div>
                    <span className="material-symbols-outlined text-gray-500">
                      {taskManagementOpen ? 'expand_less' : 'expand_more'}
                    </span>
                  </summary>
                  {taskManagementOpen && (
                    <div className="px-6 pb-6">
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-left">
                          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 w-8"></th>
                              <th className="px-4 py-3">Task Name</th>
                              <th className="px-4 py-3">Dates</th>
                              <th className="px-4 py-3">Status</th>
                              <th className="px-4 py-3 text-right">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tasks.map((task) => (
                              <React.Fragment key={task.id}>
                                <tr className="border-b hover:bg-gray-50">
                                  <td className="px-4 py-3">
                                    {task.subtasks && task.subtasks.length > 0 && (
                                      <button
                                        onClick={() => toggleTaskExpanded(task.id)}
                                        className="text-gray-500 hover:text-primary"
                                      >
                                        <span className="material-symbols-outlined text-base">
                                          {task.expanded ? 'expand_more' : 'chevron_right'}
                                        </span>
                                      </button>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 font-medium text-gray-900">
                                    {task.name}
                                    {task.description && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        {task.description}
                                      </p>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 whitespace-nowrap">
                                    {task.startDate && task.endDate ? (
                                      <span>{new Date(task.startDate).toLocaleDateString()} - {new Date(task.endDate).toLocaleDateString()}</span>
                                    ) : (
                                      <span className="text-gray-400">Not set</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center gap-2">
                                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${getStatusBadgeClass(task.status)}`}>
                                        {getStatusLabel(task.status)}
                                      </span>
                                      {task.priority === 'high' && (
                                        <span className="flex items-center text-xs text-red-600">
                                          <span className="material-symbols-outlined text-sm">arrow_upward</span>
                                          High
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                      <button
                                        onClick={() => handleEditTaskClick(task)}
                                        className="text-gray-500 hover:text-primary"
                                        title="Edit task"
                                      >
                                        <span className="material-symbols-outlined text-base">edit</span>
                                      </button>
                                      <button
                                        onClick={() => handleDuplicateTask(task)}
                                        className="text-gray-500 hover:text-primary"
                                        title="Duplicate task"
                                      >
                                        <span className="material-symbols-outlined text-base">content_copy</span>
                                      </button>
                                      <button
                                        onClick={() => handleDeleteTask(task.id)}
                                        className="text-gray-500 hover:text-red-600"
                                        title="Delete task"
                                      >
                                        <span className="material-symbols-outlined text-base">delete</span>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                                
                                {/* Expanded Subtasks */}
                                {task.expanded && task.subtasks && task.subtasks.length > 0 && (
                                  <tr className="border-b bg-gray-50">
                                    <td colSpan={5} className="px-4 py-4">
                                      <div className="ml-8 space-y-3">
                                        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                          <span className="material-symbols-outlined text-base">subdirectory_arrow_right</span>
                                          Sub-tasks ({task.subtasks.length})
                                        </h4>
                                        {task.subtasks.map((subtask) => (
                                          <div
                                            key={subtask.id}
                                            className="p-3 bg-white rounded-lg border border-gray-200"
                                          >
                                            <div className="flex items-start justify-between">
                                              <div className="flex-1">
                                                <h5 className="font-medium text-gray-900">
                                                  {subtask.title}
                                                </h5>
                                                {(subtask.startDate || subtask.endDate) && (
                                                  <p className="text-xs text-gray-500 mt-1">
                                                    {subtask.startDate && new Date(subtask.startDate).toLocaleDateString()}
                                                    {subtask.startDate && subtask.endDate && ' - '}
                                                    {subtask.endDate && new Date(subtask.endDate).toLocaleDateString()}
                                                  </p>
                                                )}
                                                
                                                {/* Checklist Items */}
                                                {subtask.checklistItems && subtask.checklistItems.length > 0 && (
                                                  <div className="mt-2 space-y-1">
                                                    <p className="text-xs font-medium text-gray-600">Checklist:</p>
                                                    {subtask.checklistItems.map((item) => (
                                                      <div key={item.id} className="flex items-center gap-2 text-sm">
                                                        <input
                                                          type="checkbox"
                                                          checked={item.checked}
                                                          readOnly
                                                          className="form-checkbox rounded text-primary w-4 h-4"
                                                        />
                                                        <span className={item.checked ? 'line-through text-gray-400' : 'text-gray-700'}>
                                                          {item.text}
                                                        </span>
                                                      </div>
                                                    ))}
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                            {tasks.length === 0 && (
                              <tr>
                                <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                                  No tasks added yet. Click "Add Task" below to get started.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <button
                        type="button"
                        onClick={handleAddTaskClick}
                        className="mt-4 flex items-center justify-center w-full gap-2 text-sm font-medium text-primary hover:text-primary/80 p-2 border-2 border-dashed border-gray-300 rounded-lg"
                      >
                        <span className="material-symbols-outlined">add</span>
                        Add Task
                      </button>
                    </div>
                  )}
                </details>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Add/Edit Task Overlay */}
      {showAddTaskOverlay && (
        <div className="fixed inset-0 bg-black/50 z-[100] overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-5">
            <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <form onSubmit={handleTaskSubmit}>
                {/* Header */}
                <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <h1 className="text-xl font-bold">{editingTask ? 'Edit Task' : 'Add New Task'}</h1>
                    <p className="text-sm text-gray-600 mt-1">
                      Schedule a task for this job with sub-tasks and checklists
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowAddTaskOverlay(false)}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    <span className="material-symbols-outlined">close</span>
                  </button>
                </div>

                {/* Body */}
                <div className="p-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                  <div className="space-y-4">
                    {/* Task Name */}
                    <div>
                      <label className="text-sm font-medium" htmlFor="task-name">
                        Task Name
                      </label>
                      <input
                        className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                        id="task-name"
                        type="text"
                        placeholder="Enter task name..."
                        value={taskName}
                        onChange={(e) => setTaskName(e.target.value)}
                      />
                    </div>

                    {/* Description */}
                    <div>
                      <label className="text-sm font-medium">Description</label>
                      <textarea
                        className="form-textarea mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                        rows={4}
                        placeholder="Add a detailed description..."
                        value={taskDescription}
                        onChange={(e) => setTaskDescription(e.target.value)}
                      />
                    </div>

                    {/* Status & Priority */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium" htmlFor="task-status">
                          Status
                        </label>
                        <select
                          className="form-select mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                          id="task-status"
                          value={taskStatus}
                          onChange={(e) => setTaskStatus(e.target.value)}
                        >
                          <option value="scheduled">Scheduled</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Priority</label>
                        <div className="flex mt-1">
                          <button
                            type="button"
                            onClick={() => setTaskPriority('low')}
                            className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm rounded-l-DEFAULT border border-gray-300 ${
                              taskPriority === 'low'
                                ? 'bg-green-50'
                                : 'bg-white'
                            }`}
                          >
                            <span className="material-symbols-outlined text-base text-green-500">
                              arrow_downward
                            </span>
                            Low
                          </button>
                          <button
                            type="button"
                            onClick={() => setTaskPriority('medium')}
                            className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm border-t border-b border-gray-300 ${
                              taskPriority === 'medium'
                                ? 'bg-yellow-50'
                                : 'bg-white'
                            }`}
                          >
                            <span className="material-symbols-outlined text-base text-yellow-500">
                              remove
                            </span>
                            Medium
                          </button>
                          <button
                            type="button"
                            onClick={() => setTaskPriority('high')}
                            className={`flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm rounded-r-DEFAULT border border-gray-300 ${
                              taskPriority === 'high'
                                ? 'bg-red-50'
                                : 'bg-white'
                            }`}
                          >
                            <span className="material-symbols-outlined text-base text-red-500">
                              arrow_upward
                            </span>
                            High
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Dates */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium" htmlFor="task-start-date">
                          Start Date
                        </label>
                        <input
                          className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                          id="task-start-date"
                          type="date"
                          value={taskStartDate}
                          onChange={(e) => setTaskStartDate(e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium" htmlFor="task-end-date">
                          End Date
                        </label>
                        <input
                          className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                          id="task-end-date"
                          type="date"
                          value={taskEndDate}
                          onChange={(e) => setTaskEndDate(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Sub-tasks */}
                    <div>
                      <h4 className="font-semibold text-sm mb-2">Sub-tasks</h4>
                      <div className="space-y-4">
                        {taskSubtasks.map((subtask) => (
                          <div
                            key={subtask.id}
                            className="p-4 rounded-lg bg-gray-50 border border-gray-300"
                          >
                            <div className="space-y-3">
                              <div>
                                <label className="text-sm font-medium">Subtask Name</label>
                                <input
                                  className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                                  type="text"
                                  placeholder="Enter subtask name..."
                                  value={subtask.title}
                                  onChange={(e) =>
                                    updateSubtask(subtask.id, 'title', e.target.value)
                                  }
                                />
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <label className="text-xs font-medium text-gray-600">
                                    Start Date
                                  </label>
                                  <input
                                    className="form-input mt-1 w-full text-sm rounded-DEFAULT border-gray-300 bg-white"
                                    type="date"
                                    value={subtask.startDate}
                                    onChange={(e) =>
                                      updateSubtask(subtask.id, 'startDate', e.target.value)
                                    }
                                  />
                                </div>
                                <div>
                                  <label className="text-xs font-medium text-gray-600">
                                    End Date
                                  </label>
                                  <input
                                    className="form-input mt-1 w-full text-sm rounded-DEFAULT border-gray-300 bg-white"
                                    type="date"
                                    value={subtask.endDate}
                                    onChange={(e) =>
                                      updateSubtask(subtask.id, 'endDate', e.target.value)
                                    }
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="text-xs font-medium text-gray-600">
                                  Checklist
                                </label>
                                <div className="mt-1 space-y-1">
                                  {subtask.checklistItems.map((item) => (
                                    <label
                                      key={item.id}
                                      className="flex items-center gap-2 text-sm p-1"
                                    >
                                      <input
                                        className="form-checkbox rounded text-primary"
                                        type="checkbox"
                                        checked={item.checked}
                                        onChange={(e) =>
                                          updateChecklistItem(
                                            subtask.id,
                                            item.id,
                                            'checked',
                                            e.target.checked
                                          )
                                        }
                                      />
                                      <input
                                        className="flex-1 border-0 p-0 text-sm bg-transparent focus:ring-0"
                                        type="text"
                                        placeholder="Enter checklist item..."
                                        value={item.text}
                                        onChange={(e) =>
                                          updateChecklistItem(
                                            subtask.id,
                                            item.id,
                                            'text',
                                            e.target.value
                                          )
                                        }
                                      />
                                      <button
                                        type="button"
                                        onClick={() => removeChecklistItem(subtask.id, item.id)}
                                        className="p-1 text-gray-500 hover:text-error"
                                      >
                                        <span className="material-symbols-outlined text-base">
                                          delete
                                        </span>
                                      </button>
                                    </label>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => addChecklistItem(subtask.id)}
                                  className="flex items-center gap-1 text-xs font-medium text-primary hover:underline mt-1"
                                >
                                  <span className="material-symbols-outlined text-sm">add</span>
                                  Add checklist item
                                </button>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeSubtask(subtask.id)}
                                className="flex items-center gap-1 text-xs font-medium text-error hover:underline"
                              >
                                <span className="material-symbols-outlined text-sm">delete</span>
                                Remove Subtask
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addSubtask}
                        className="w-full flex items-center justify-center gap-2 p-2 mt-3 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200"
                      >
                        <span className="material-symbols-outlined text-base">add</span>
                        Add Sub-task
                      </button>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="p-4 bg-white border-t border-gray-200 flex justify-between items-center">
                  <div>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-DEFAULT text-gray-700 hover:bg-gray-100"
                    >
                      <span className="material-symbols-outlined text-base">bookmark_add</span>
                      Save as Template
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setShowAddTaskOverlay(false)}
                      className="px-4 py-2 text-sm font-bold rounded-DEFAULT text-gray-700 bg-gray-100 hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 text-sm font-bold rounded-DEFAULT bg-primary text-white hover:bg-primary/90"
                    >
                      {editingTask ? 'Update Task' : 'Add Task'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Create New Client Modal */}
      {showNewClientModal && (
        <div className="fixed inset-0 bg-black/50 z-[200] overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center p-5">
            <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-xl font-bold">Create New Client</h2>
                <button
                  type="button"
                  onClick={() => setShowNewClientModal(false)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  <span className="material-symbols-outlined">close</span>
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                {clientError && (
                  <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{clientError}</div>
                )}
                <div>
                  <label className="text-sm font-medium" htmlFor="new-client-name">
                    Client Name *
                  </label>
                  <input
                    type="text"
                    id="new-client-name"
                    className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                    placeholder="Enter client name..."
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    autoFocus
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium" htmlFor="new-client-email">
                    Email
                  </label>
                  <input
                    type="email"
                    id="new-client-email"
                    className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                    placeholder="client@example.com"
                    value={newClientEmail}
                    onChange={(e) => setNewClientEmail(e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium" htmlFor="new-client-phone">
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="new-client-phone"
                    className="form-input mt-1 w-full rounded-DEFAULT border-gray-300 bg-white"
                    placeholder="(555) 555-5555"
                    value={newClientPhone}
                    onChange={(e) => setNewClientPhone(e.target.value)}
                  />
                </div>
              </div>
              
              <div className="p-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowNewClientModal(false)}
                  disabled={creatingClient}
                  className="px-4 py-2 text-sm font-bold rounded-DEFAULT text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateNewClient}
                  disabled={!newClientName.trim() || creatingClient}
                  className="px-6 py-2 text-sm font-bold rounded-DEFAULT bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {creatingClient && (
                    <span className="animate-spin">⏳</span>
                  )}
                  {creatingClient ? 'Creating...' : 'Create Client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteTaskId !== null && (
        <ConfirmDialog
          title="Delete Task"
          message="Are you sure you want to delete this task?"
          confirmLabel="Delete"
          variant="danger"
          onConfirm={() => {
            setTasks(tasks.filter(t => t.id !== confirmDeleteTaskId));
            setConfirmDeleteTaskId(null);
          }}
          onCancel={() => setConfirmDeleteTaskId(null)}
        />
      )}
    </>
  );
};



